import React from 'react'
import { t } from 'ttag'
import cx from 'classnames'
import { Box } from 'components/layout/Box/Box'
import { Modal } from 'components/shared/Modal/Modal'
import { TabsBar } from 'components/shared/TabsBar/TabsBar'
import { PersonalityFactors } from 'components/modals/TestsReportModal/tabs/PersonalityFactors/PersonalityFactors'
import { useJobSeekerResultsModalState } from './hooks'
import { ScatterPlot } from 'components/shared/ScatterPlot/ScatterPlot'
import { TalentAreasTable } from './components/TalentAreasTable/TalentAreasTable'
import { AssessmentSummary } from './components/AssessmentSummary/AssessmentSummary'
import { TopAreas } from './components/TopAreas/TopAreas'
import { GmaModal } from 'components/modals/GmaModal/GmaModal'
import { Button } from 'components/shared/Button/Button'
import { FeatureFlag } from 'components/auth/FeatureFlag'
import { TAB_NAMES, getTabs, getJobSeekerPersonalityFactors } from './model'

import styles from './JobSeekerResultsModal.module.scss'

export const JobSeekerResultsModal = ({
  onCancel,
  onClose,
  jobSeeker,
  isOpen,
}) => {
  const { state, handlers } = useJobSeekerResultsModalState({
    isOpen,
    jobSeeker,
  })

  const {
    talentData,
    rawData,
    ordering,
    categoriesFilter,
    valueFilter,
    isGmaOpen,
    gmaScore,
    showGma,
    showGmaToggle,
    language,
    candidateResponse,
    activeTab,
    personalityTab,
    selectedCandidates,
  } = state
  const {
    setOrder,
    updateValueFilter,
    updateCategoryFilter,
    handleClose,
    handleConfirm,
    handleOpen,
    setGmaScore,
    setShowGma,
    handleTab,
    setPersonalityTab,
    setSelectedCandidates,
  } = handlers

  const tabs = getTabs()
  const panelScrollRef = React.useRef(null)

  const overviewPanel = (
    <div className={cx(styles.innerContent, styles.innerContentTalent)}>
      {showGmaToggle && (
        <div className={styles.gmaToggleRow}>
          <div className={styles.gmaToggle}>
            <Button
              label={t`Without GMA`}
              className={cx(styles.gmaButton, { [styles.gmaButtonActive]: !showGma })}
              variant={!showGma ? 'primary filled' : 'outlined'}
              onClick={() => setShowGma(false)}
            />
            <Button
              label={t`With GMA`}
              className={cx(styles.gmaButton, { [styles.gmaButtonActive]: showGma })}
              variant={showGma ? 'primary filled' : 'outlined'}
              onClick={() => setShowGma(true)}
            />
          </div>
        </div>
      )}
      <div className={styles.topSection}>
        <div className={styles.plotSection}>
          <ScatterPlot
            data={rawData.map(talent => ({
              dataPoint: [talent.personalityScore, talent.interestScore],
              description: talent.description,
              area: talent.area,
            }))}
          />
        </div>
        <AssessmentSummary data={rawData} jobSeeker={jobSeeker} />
      </div>
      <TopAreas data={rawData} jobSeeker={jobSeeker} />
      <TalentAreasTable
        data={talentData}
        contributionSignificance={
          candidateResponse?.talent_scores?.contribution_significance
        }
        scrollContainerRef={panelScrollRef}
        setOrder={setOrder}
        ordering={ordering}
        categoriesFilter={categoriesFilter}
        valueFilter={valueFilter}
        updateValueFilter={updateValueFilter}
        updateCategoryFilter={updateCategoryFilter}
      />
    </div>
  )

  const personalityPanel = candidateResponse ? (
    <div className={cx(styles.innerContent, styles.innerContentPersonality)}>
      <PersonalityFactors
        factors={getJobSeekerPersonalityFactors()}
        language={language}
        scores={[candidateResponse.scores]}
        response={[candidateResponse]}
        personalityTab={personalityTab}
        setPersonalityTab={setPersonalityTab}
        selectedCandidates={selectedCandidates}
        setSelectedCandidates={setSelectedCandidates}
        candidates={[jobSeeker]}
        name={[candidateResponse.name]}
      />
    </div>
  ) : null

  return (
    <Modal
      cancelLabel={t`Close`}
      className={styles.modal}
      {...{
        onCancel,
        onClose,
        isOpen,
      }}
    >
      <Modal.Content className={styles.content}>
        <TabsBar
          className={styles.tabs}
          tabClassName={styles.tab}
          onClick={handleTab}
          {...{ activeTab, tabs }}
        />
        <Box ref={panelScrollRef} className={styles.panel} direction="column">
          {activeTab === TAB_NAMES.OVERVIEW ? overviewPanel : personalityPanel}
        </Box>
      </Modal.Content>

      <Modal.Footer className={styles.footer} />

      {isGmaOpen ? (
        <GmaModal
          onCancel={handleClose}
          onClose={handleClose}
          onConfirm={handleConfirm}
          jobSeeker={jobSeeker}
          gmaScore={gmaScore}
          setGmaScore={setGmaScore}
          isOpen
        />
      ) : null}
    </Modal>
  )
}
